import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import {
  ReservationDashboardRow,
  useInvalidateReservationDashboard,
} from '@/api/reservations/get-reservation-dashboard'
import {
  RESERVATION_DETAILS_QUERY_KEY,
  ReservationDetails,
} from '@/api/reservations/get-reservation-details'
import { RESERVATION_TRANSACTIONS_QUERY_KEY } from '@/api/reservations/get-reservation-transactions'
import ConfirmPopupBody from '@/components/Reservations/ReservationsList/ConfirmPopupBody'
import { ConfirmationPopup } from '@/components/ui/confirmation-popup'
import { DEFAULT_TZ } from '@/constants/date'
import { toastFactory } from '@/factories'
import { canIssueRefund } from '@/helpers/helpers'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { setRequestStatus } from '@/reducers/Organisation/reservationsReducer'
import { selectUserData } from '@/reducers/userDataReducer'
import { resetQueries } from '@/utils/api'

import { RefundState, RefundStateEnum } from './reservations-dashboard.constants'

interface DeclineReservationConfirmationPopupProps {
  reservation: ReservationDashboardRow | ReservationDetails
  onClose: VoidFunction
  allowPartialRefund?: boolean
  isFree?: boolean
  onConfirm?: (confirmParams: {
    refundState: RefundState
    refundAmount?: string
    cancelNote: string
  }) => void
}

export const DeclineReservationConfirmationPopup = ({
  reservation,
  onClose,
  allowPartialRefund,
  isFree,
  onConfirm,
}: DeclineReservationConfirmationPopupProps) => {
  const { t } = useTranslation(['reservations', 'common'])
  const dispatch = useDispatch() as any
  const activeBuilding = useSelector(selectActiveBuilding)
  const userData = useSelector(selectUserData)
  const invalidateReservations = useInvalidateReservationDashboard()
  const timezone = activeBuilding?.timezone ?? DEFAULT_TZ

  const isRefundAllowed = canIssueRefund(userData?.user?.role, reservation.date, timezone)

  const [refundState, setRefundState] = useState<RefundState>(
    isFree || !isRefundAllowed ? RefundStateEnum.enum.no_refund : RefundStateEnum.enum.refund,
  )
  const [cancelNote, setCancelNote] = useState('')
  const [refundAmount, setRefundAmount] = useState('')

  const total = ('total' in reservation ? reservation.total : reservation.chargeAmount) ?? 0
  const hasTotal = total > 0
  const isRefundSelected = refundState === RefundStateEnum.enum.refund

  const confirmText: string =
    hasTotal && isRefundAllowed && isRefundSelected
      ? t('reservations:dashboard.declineModal.confirm.withRefund')
      : t('reservations:dashboard.declineModal.confirm.withoutRefund')

  const handleConfirm = async () => {
    if (onConfirm) {
      onConfirm({
        cancelNote,
        refundAmount,
        refundState,
      })

      return
    }

    try {
      await dispatch(
        setRequestStatus(
          [reservation.id],
          'decline',
          isRefundAllowed && isRefundSelected,
          cancelNote,
        ),
      )
      await resetQueries([RESERVATION_DETAILS_QUERY_KEY], [RESERVATION_TRANSACTIONS_QUERY_KEY])
      invalidateReservations()
      toastFactory.success(t('reservations:dashboard.declineModal.toasts.success'))
    } catch {
      toastFactory.error(t('reservations:dashboard.declineModal.toasts.error'))
    }
  }

  useEffect(() => {
    setRefundAmount(refundState === RefundStateEnum.enum.refund ? total.toString() : '')
  }, [refundState])

  return (
    <ConfirmationPopup
      title={t('reservations:shared.confirmationTitle')}
      cancelText={t('common:actions.cancel')}
      confirmText={confirmText}
      variant="danger"
      onClose={onClose}
      onConfirm={handleConfirm}
    >
      <ConfirmPopupBody
        allowPartialRefund={allowPartialRefund}
        title={
          hasTotal
            ? t('reservations:dashboard.declineModal.body.withPayment')
            : t('reservations:dashboard.declineModal.body.withoutPayment')
        }
        shouldRenderRefundOptions={hasTotal}
        refundBlocked={!isRefundAllowed}
        refundState={refundState}
        refundAmount={refundAmount}
        cancelNote={cancelNote}
        paidAmount={allowPartialRefund ? total : undefined}
        onRefundStateChange={((e: any) => setRefundState(e.target.value)) as () => void}
        onCancelNoteChange={((e: any) => setCancelNote(e.target.value)) as () => void}
        onRefundAmountChange={((amount: any) => setRefundAmount(amount)) as () => void}
      />
    </ConfirmationPopup>
  )
}
